import type { AiChatMessage } from "../api/ai.types";
import type { AnticipationMode, AnticipationTurn } from "../api/anticipation-client";

export const ANTICIPATION_PAUSE_MS = 900;
export const ANTICIPATION_CONTINUATION_MS = 1_500;
export const ANTICIPATION_MIN_GAP_MS = 4_500;

const followUp = /^(?:sí|si|vale|claro|continúa|continua|sigue|adelante|y entonces|cuéntame más|cuentame mas)[.!?…]*$/iu;
const credentials = /\b(?:contraseña|password|api[_ -]?key|secret[_ -]?key|token de acceso|código de verificación|codigo otp|pin bancario)\b|sk-(?:proj-)?[a-z0-9_-]{12,}/iu;
const code = /\x60{3}|<script\b|\b(?:SELECT|INSERT|DELETE|UPDATE)\s+.+\b(?:FROM|INTO|SET)\b/iu;

export function previewContext(history: readonly AiChatMessage[]): AnticipationTurn[] {
  return history.filter(item => (item.role === "user" || item.role === "assistant") &&
    !item.image && typeof item.content === "string")
    .slice(-8).map(item => ({
      role: item.role as "user" | "assistant",
      content: item.content.slice(0, 1000)
    }));
}

export function canAnticipateTyping(draft: string, history: readonly AiChatMessage[]): boolean {
  const text = draft.trim();
  if (!text || text.length > 1_800 || credentials.test(text) || code.test(text)) return false;
  if (history.some(item => credentials.test(item.content))) return false;
  if (text.length >= 18) return true;
  return followUp.test(text) && history.some(item => item.role === "user") &&
    history.some(item => item.role === "assistant");
}

export function canAnticipateContinuation(
  history: readonly AiChatMessage[], hasFreshResponse: boolean
): boolean {
  if (!hasFreshResponse || history.length < 3) return false;
  const last = history[history.length - 1];
  return last?.role === "assistant" && !last.image &&
    history.some(item => item.role === "user") &&
    !history.some(item => credentials.test(item.content));
}

export function anticipationDelay(
  mode: AnticipationMode, lastStartedAt: number, now: number
): number {
  const base = mode === "typing" ? ANTICIPATION_PAUSE_MS : ANTICIPATION_CONTINUATION_MS;
  return Math.max(base, ANTICIPATION_MIN_GAP_MS - Math.max(0, now - lastStartedAt));
}


/**
 * Double clutch: do not throw away an expensive local preview simply because
 * the user adds a few words to the same unfinished sentence. A changed premise,
 * negation or large extension invalidates the speculative work immediately.
 */
export function keepsSameDraftPrefix(started: string, current: string): boolean {
  const prefix = started.trim();
  const now = current.trim();
  if (prefix.length < 14 || !now.startsWith(prefix) || now.length - prefix.length > 140) return false;
  const extra = now.slice(prefix.length);
  if (/\b(?:no|nunca|pero|excepto|sin|aunque|cancela|olvida|contrario)\b/iu.test(extra)) return false;
  return true;
}
